import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { AuthSyncService } from './auth-sync.service';
import { Observable, Subject, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { MessageService } from 'primeng/api';
import { Injectable, Injector } from '@angular/core';
import { AuthService } from '../swagger';
import { Router } from '@angular/router';
import { NEVER } from 'rxjs'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isLoggedOut = false;
  constructor(
    private injector: Injector,
    private authApi: AuthService,
    private messageService: MessageService,
    private router: Router
  ) {}

  requestInProgress = false;
  refreshToken: Subject<string> = new Subject<string>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith(environment.BASE_PATH)) {
      // get snapshot ->
      // dashboard -> do not add to session
      // login -> redireting and not logged -> add to session
      // on login -> check session -> redirect
      return next.handle(req).pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) {
            return throwError(() => err);
          }

          // 1. Handle Internet Connection
          if (err.status === 0) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Internet not connected' });
            return NEVER; // Stops propagation to component
          }

          // 2. Handle 401 (Unauthorized / Session Expired)
          if (err.status === 401) {
            // Don't logout on login endpoint - let component handle login errors
            if (req.url.includes('/auth/login')) return throwError(() => err);
            
            // Only logout if it's a session expiration error (has SESSION_EXPIRED code)
            // Other 401s (like customer portal errors) should be handled by components
            if (err.error?.code === 'SESSION_EXPIRED') {
              const errorMessage = err.error?.message || 'Your session has expired. Please log in again.';
              // Show message before navigation
              this.messageService.add({ severity: 'warn', summary: 'Session Expired', detail: errorMessage });
              const authSync = this.injector.get(AuthSyncService);
              // Use setTimeout to ensure message is displayed before navigation
              setTimeout(() => {
                authSync.logout(true);
              }, 300);
              return NEVER;
            }
            
            // For other 401s without SESSION_EXPIRED code, let component handle it
            return throwError(() => err);
          }

          // 3. Handle 410 (Token Expired / Gone)
          if (err.status === 410) {
            return this.handle410Error(req, next);
          }

          // 4. Handle 403 (Forbidden)
          if (err.status === 403) {
            if (req.url.includes('/auth/login')) return throwError(() => err);

            if (err.error?.code === 'USER_NOT_ACTIVE') {
              const authSync = this.injector.get(AuthSyncService);
              authSync.logout();
              this.messageService.add({ severity: 'warn', detail: 'Your account is not active. Please contact administrator.' });
            } else {
              this.router.navigate(['/org/access-denied'], { replaceUrl: true });
            }
            return NEVER;
          }

          // 4. Handle 406 (Not Acceptable)
          if (err.status === 406) {
            this.router.navigate(['/org/access-denied'], { replaceUrl: true });
            return NEVER;
          }

          return throwError(() => err);
        })
      );
    }
    return next.handle(req);
  }

  private handle410Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      return this.authApi.refreshToken().pipe(
        switchMap(({ token, user }) => {
          const authSync = this.injector.get(AuthSyncService);
          const http = this.injector.get(HttpClient);
          authSync.setToken(token!);
          authSync.setState(user!);
          this.refreshToken.next(token!);
          this.requestInProgress = false;

          return http.request(req.clone({
            setHeaders: { Token: token! },
            withCredentials: true,
          }));
        }),
        catchError((refreshErr) => {
          this.requestInProgress = false;
          // If refresh fails, redirect to login and swallow error
          this.router.navigate(['/login']);
          return NEVER; 
        })
      );
    } else {
      return this.refreshToken.pipe(
        switchMap(token => {
          const http = this.injector.get(HttpClient);
          return http.request(req.clone({
            setHeaders: { Token: token! },
            withCredentials: true,
          }));
        })
      );
    }
  }

  public resetLogoutFlag() {
    this.isLoggedOut = false;
  }
}
